import { Card, wantsCardImage } from '@lingoping/core';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { getSupabase, isSupabaseConfigured } from '../config/supabase.js';
import { StorageManager } from '../storage/index.js';
import { hasUsableImage } from '../utils/images.js';
import { isAccessLocked, noteAccessLocked } from './access.js';

// A handful of parallel requests at most: a 50-word import must not fire 50 status checks at once.
const MAX_PARALLEL = 2;

// `card-image` answers in a couple of fast queries and, if nobody is already on it, starts
// generation in the background and replies 202 pending - so while a page is open we poll it a few
// times with backoff instead of holding one request open. A word usually already has a head start:
// `generate-card` kicks off the same generation right after it writes the word's text, so by the
// time a card is shown here the picture may already be most of the way done.
export const POLL_BACKOFF_MS = [1500, 2500, 4000, 6000, 8000];

const waiting: Array<() => void> = [];
let running = 0;
let dailyLimitHitOn: string | null = null;

async function withSlot<T>(task: () => Promise<T>): Promise<T> {
  if (running >= MAX_PARALLEL) await new Promise<void>((resolve) => waiting.push(resolve));
  running++;
  try {
    return await task();
  } finally {
    running--;
    waiting.shift()?.();
  }
}

const today = () => new Date().toISOString().slice(0, 10);
const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Makes sure a noun/verb card has its picture. `card-image` returns the shared image as soon as
 * it's ready (from this word's own generation, kicked off by `generate-card` right after the card
 * text was written, or another user's), and otherwise reports 202 pending, in which case this polls
 * a few times with backoff while this page is open. Stores the URL on the local card and returns
 * the updated card, or null when nothing changed (not a noun/verb, already has an image, signed
 * out, offline, daily cap reached, generation failed, or still pending when polling gave up - the
 * background retry alarm tries it again later). Also null, and nothing asked, while the learner
 * has no access: a picture is background work, so the lock is only saved for the screens
 * (services/access.ts), not thrown at whoever started it.
 */
export async function ensureCardImage(card: Card): Promise<Card | null> {
  if (!wantsCardImage(card.part_of_speech) || hasUsableImage(card)) return null;
  // The server draws only words whose text it generated itself (older cards are upgraded first).
  if (card.linguistic_meta?.content_source !== 'ai') return null;
  if (dailyLimitHitOn === today()) return null;
  if (await isAccessLocked()) return null;

  const profile = await StorageManager.getProfile();
  const key = profile.supabase_publishable_key || profile.supabase_anon_key;
  if (!isSupabaseConfigured(profile.supabase_url, key)) return null;
  const supabase = getSupabase(profile.supabase_url, key);
  const { data: session } = await supabase.auth.getSession();
  if (!session.session) return null;

  const lemma = card.lemma ?? card.front_text;
  const body = { lemma, language: card.target_language || 'de' };

  for (let attempt = 0; attempt <= POLL_BACKOFF_MS.length; attempt++) {
    const { data, error } = await withSlot(() =>
      supabase.functions.invoke<{ image_url?: string; status?: string }>('card-image', { body })
    );

    if (error) {
      if (await noteAccessLocked(error)) return null;
      if (error instanceof FunctionsHttpError && error.context?.status === 429) {
        dailyLimitHitOn = today();
      }
      console.warn(`[LingoPing] No image for "${lemma}":`, error.message);
      return null;
    }
    const url = data?.image_url;
    if (url && url.startsWith('https://')) {
      // Only the picture is changed, so a review that happened meanwhile is not overwritten.
      // Null when the card was removed while the picture was being made.
      return StorageManager.updateCardContent(card.id, { image_url: url });
    }
    if (data?.status !== 'pending') return null; // not_applicable, content_missing, etc.
    if (attempt < POLL_BACKOFF_MS.length) await wait(POLL_BACKOFF_MS[attempt]);
  }
  return null; // still generating; the background retry alarm will check back later
}

// ---------------------------------------------------------------------------------------------
// Background retry: a card must not stay without a picture forever just because no page was open
// long enough to see it finish. `background/index.ts` runs this periodically (chrome.alarms).
// Per-card backoff is kept in chrome.storage.local under our own key (never packages/extension/src
// /storage/index.ts, which another engineer owns), so a word that keeps failing is not retried on
// every alarm tick forever.
// ---------------------------------------------------------------------------------------------

const RETRY_STATE_KEY = 'lingoping_image_retry_v1';
/** 5m, 15m, 30m, 1h, 2h, then every 6h - spaced out so a persistently failing word (offline,
 * rejected content, daily cap) does not get hammered, but still gets picked up eventually. */
export const RETRY_BACKOFF_MS = [5, 15, 30, 60, 120, 360].map((m) => m * 60_000);
/** At most this many cards per alarm tick, so a large backlog cannot flood the daily image cap. */
const MAX_CANDIDATES_PER_RUN = 5;

export interface ImageRetryEntry {
  attempts: number;
  nextAttemptAt: number;
}
export type ImageRetryState = Record<string, ImageRetryEntry>;

/** `attempts` counts failures so far (1 after the first); the 1st failure gets the shortest step. */
function backoffFor(attempts: number): number {
  return RETRY_BACKOFF_MS[Math.min(attempts - 1, RETRY_BACKOFF_MS.length - 1)];
}

async function readRetryState(): Promise<ImageRetryState> {
  if (typeof chrome === 'undefined' || !chrome.storage?.local) return {};
  const stored = await chrome.storage.local.get(RETRY_STATE_KEY);
  return (stored[RETRY_STATE_KEY] as ImageRetryState) || {};
}

async function writeRetryState(state: ImageRetryState): Promise<void> {
  if (typeof chrome === 'undefined' || !chrome.storage?.local) return;
  await chrome.storage.local.set({ [RETRY_STATE_KEY]: state });
}

/**
 * Picks the noun/verb cards worth retrying now: no picture yet, text came from the server, and
 * either never tried before or past their backoff. Pure function, easy to test on its own.
 */
export function selectCardsForBackgroundRetry(
  cards: Card[],
  state: ImageRetryState,
  now: number = Date.now(),
  limit: number = MAX_CANDIDATES_PER_RUN
): Card[] {
  return cards
    .filter(
      (c) =>
        !c.retired_at &&
        c.content_status !== 'pending' &&
        wantsCardImage(c.part_of_speech) &&
        !hasUsableImage(c) &&
        c.linguistic_meta?.content_source === 'ai' &&
        (!state[c.id] || state[c.id].nextAttemptAt <= now)
    )
    .slice(0, limit);
}

/**
 * Retries a batch of local cards still missing their picture. Called on a `chrome.alarms` tick;
 * safe to call anytime (no-op if there is nothing to retry). Returns how many cards got an image.
 */
export async function runBackgroundImageRetry(): Promise<number> {
  // No access: no pictures to ask for, and no failed try to count.
  if (await isAccessLocked()) return 0;
  const cards = await StorageManager.getCards();
  const state = await readRetryState();
  const candidates = selectCardsForBackgroundRetry(cards, state);
  if (candidates.length === 0) return 0;

  let succeeded = 0;
  const nextState: ImageRetryState = { ...state };
  for (const card of candidates) {
    const updated = await ensureCardImage(card).catch((err) => {
      console.warn('[LingoPing] Background image retry failed:', (err as Error).message);
      return null;
    });
    // Locked in the middle of the run: stop, and do not count it against the card.
    if (!updated && (await isAccessLocked())) break;
    if (updated) {
      succeeded++;
      delete nextState[card.id];
    } else {
      const attempts = (state[card.id]?.attempts ?? 0) + 1;
      nextState[card.id] = { attempts, nextAttemptAt: Date.now() + backoffFor(attempts) };
    }
  }
  // Drop entries for cards that no longer exist or now have a picture some other way.
  const liveIds = new Set(cards.map((c) => c.id));
  for (const id of Object.keys(nextState)) {
    if (!liveIds.has(id)) delete nextState[id];
  }
  await writeRetryState(nextState);
  return succeeded;
}
